import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    // Remove trailing slash
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    // Remove /api suffix if present (we'll add it back)
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return ''; // Empty string means relative URL - will become /api/... when concatenated
  }
  // Development fallback
  return 'http://localhost:3000';
};

const DashboardCards = () => {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/dashboard-cards/admin/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        setCards(response.data.data);
        setHasChanges(false);
      } else {
        setError('Failed to load dashboard cards');
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard cards');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (cardKey) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.card_key === cardKey
          ? { ...card, is_visible: !card.is_visible }
          : card
      )
    );
    setHasChanges(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newCards = [...cards];
    const temp = newCards[index].display_order;
    newCards[index].display_order = newCards[index - 1].display_order;
    newCards[index - 1].display_order = temp;
    setCards(newCards);
    setHasChanges(true);
  };

  const handleMoveDown = (index) => {
    if (index === cards.length - 1) return;
    const newCards = [...cards];
    const temp = newCards[index].display_order;
    newCards[index].display_order = newCards[index + 1].display_order;
    newCards[index + 1].display_order = temp;
    setCards(newCards);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const baseUrl = getApiBaseUrl();
      const cardsToUpdate = cards.map((card) => ({
        card_key: card.card_key,
        is_visible: card.is_visible,
        display_order: card.display_order,
      }));

      const response = await axios.put(
        `${baseUrl}/api/dashboard-cards/admin/bulk`,
        { cards: cardsToUpdate },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setSuccess('Dashboard cards updated successfully!');
        setHasChanges(false);
        // Refresh to get updated data
        setTimeout(() => {
          fetchCards();
        }, 500);
      } else {
        setError('Failed to update dashboard cards');
      }
    } catch (err) {
      console.error('Error saving cards:', err);
      setError(err.response?.data?.message || 'Failed to update dashboard cards');
    } finally {
      setSaving(false);
    }
  };

  const sortedCards = [...cards].sort((a, b) => a.display_order - b.display_order);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard Cards Control
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Control which cards are visible on the user dashboard and their display order.
          Hidden cards will not be accessible via direct links.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Card Visibility Settings
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchCards}
                    sx={{ mr: 2 }}
                    disabled={saving}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Box>

              {hasChanges && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You have unsaved changes. Click "Save Changes" to apply them.
                </Alert>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="5%">Order</TableCell>
                      <TableCell width="25%">Card Title</TableCell>
                      <TableCell width="30%">Path</TableCell>
                      <TableCell width="15%" align="center">
                        Visibility
                      </TableCell>
                      <TableCell width="15%" align="center">
                        Status
                      </TableCell>
                      <TableCell width="10%" align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCards.map((card, index) => (
                      <TableRow key={card.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {card.display_order}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight={500}>
                            {card.card_title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {card.card_path}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={card.is_visible}
                                onChange={() => handleToggleVisibility(card.card_key)}
                                color="primary"
                              />
                            }
                            label={card.is_visible ? 'Visible' : 'Hidden'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={card.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            label={card.is_visible ? 'Visible' : 'Hidden'}
                            color={card.is_visible ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            color="primary"
                          >
                            <ArrowUpwardIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === sortedCards.length - 1}
                            color="primary"
                          >
                            <ArrowDownwardIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Instructions
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Toggle the switch to show/hide cards from the user dashboard
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Use the arrow buttons to change the display order of cards
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Hidden cards will not be accessible via direct links - users will be redirected
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Click "Save Changes" to apply your settings
                  </Typography>
                </li>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DashboardCards;

